import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, CheckCircle, LogIn, XCircle, Search, Calendar, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import AddTenantModal from './AddTenantModal';
import CancelBookingModal from './CancelBookingModal';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';
import { useEmptyStateTracker } from '../../hooks/useEmptyStateTracker';
import { track, trackFirstTime } from '../../services/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending:   { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
  confirmed: { bg: 'bg-blue-100',   text: 'text-blue-800',   label: 'Confirmed' },
  moved_in:  { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Moved In' },
  converted: { bg: 'bg-green-100',  text: 'text-green-800',  label: 'Moved In' },
  cancelled: { bg: 'bg-gray-100',   text: 'text-gray-700',   label: 'Cancelled' },
};

const BookingsList = () => {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, confirmed: 0, converted: 0, total_tokens: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hasProperties, setHasProperties] = useState(true);
  const [movingInBooking, setMovingInBooking] = useState(null);
  const [cancellingBooking, setCancellingBooking] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, sRes, pRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setBookings(bRes.data);
      setSummary(sRes.data);
      setHasProperties(pRes.data.length > 0);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load bookings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Hot refresh on any booking / tenant / lead change anywhere
  useDataSync([EVT.BOOKING, EVT.TENANT, EVT.LEAD], fetchData);
  useEmptyStateTracker('bookings', bookings.length, loading);

  const handleConfirm = async (b) => {
    try {
      await axios.post(`${API}/bookings/${b.id}/confirm`, {}, { headers: { Authorization: `Bearer ${token}` } });
      emit(EVT.BOOKING, { action: 'confirmed', id: b.id });
      track('booking_confirmed', { id: b.id });
      toast({ title: 'Booking Confirmed' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handleConvert = async (b) => {
    // Open the pre-filled Add Tenant modal for owner to review/edit, then submit
    // posts to /api/bookings/{id}/move-in which creates tenant + auto-logs token payment.
    if (!b.room_id || !b.property_id) {
      toast({ title: 'Assign a room first', variant: 'destructive' });
      return;
    }
    setMovingInBooking(b);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/bookings/${deleting.id}`, { headers: { Authorization: `Bearer ${token}` } });
      emit(EVT.BOOKING, { action: 'deleted', id: deleting.id });
      emit(EVT.ROOM,    { action: 'booking_released', room_id: deleting.room_id });
      toast({ title: 'Booking Deleted' });
      setDeleting(null);
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = bookings.filter(b => {
    const s = search.toLowerCase();
    const matchesSearch = !s || b.name?.toLowerCase().includes(s) || b.phone?.includes(s) || b.property_name?.toLowerCase().includes(s);
    const matchesStatus = !statusFilter || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="bookings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Bookings</h1>
          <p className="text-gray-600 mt-1">Pre-tenancy reservations & token amounts</p>
        </div>
        <Button data-testid="add-booking-btn" onClick={() => { setEditing(null); setShowForm(true); }} disabled={!hasProperties} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40">
          <Plus size={18} className="mr-2" /> New Booking
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="p-4 border-l-4 border-l-blue-500">
          <p className="text-xs text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary.total}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-yellow-500">
          <p className="text-xs text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-yellow-700 mt-1">{summary.pending}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-blue-500">
          <p className="text-xs text-gray-500">Confirmed</p>
          <p className="text-2xl font-bold text-blue-700 mt-1">{summary.confirmed}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-green-500">
          <p className="text-xs text-gray-500">Moved In</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{summary.moved_in ?? summary.converted ?? 0}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-purple-500">
          <p className="text-xs text-gray-500">Total Token (₹)</p>
          <p className="text-2xl font-bold text-purple-700 mt-1">₹{(summary.total_tokens || 0).toLocaleString()}</p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="bookings-search"
            type="text"
            placeholder="Search by name, phone, property..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
        <select
          data-testid="bookings-status-filter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="moved_in">Moved In</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="bookings-empty">
          <Calendar size={64} className="mx-auto text-gray-300 mb-4" />
          {!hasProperties ? (
            <>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Add a property first</h3>
              <p className="text-gray-600 mb-4">Bookings are attached to a property. Create one before taking reservations.</p>
              <Button onClick={() => window.location.href = '/manager/property/list'} className="bg-blue-600 hover:bg-blue-700" data-testid="bookings-add-property-cta">
                <Plus size={18} className="mr-2" /> Add Property
              </Button>
            </>
          ) : (
            <>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Bookings Yet</h3>
              <p className="text-gray-600 mb-4">Create your first booking to track pre-tenancy reservations</p>
              <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus size={18} className="mr-2" /> New Booking
              </Button>
            </>
          )}
        </Card>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Guest</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Property / Room</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Booked</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Check-in</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Token</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rent / Dep</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(b => {
                const s = STATUS_CONFIG[b.status] || STATUS_CONFIG.pending;
                return (
                  <tr key={b.id} className="hover:bg-blue-50/30" data-testid={`booking-row-${b.id}`}>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-xs font-bold">
                          {b.name?.charAt(0)}
                        </div>
                        <p className="font-semibold text-gray-900 truncate max-w-[120px]">{b.name}</p>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-xs text-gray-700">{b.phone}</p>
                      {b.email && <p className="text-[11px] text-gray-500 truncate max-w-[150px]">{b.email}</p>}
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-xs font-medium text-gray-800 truncate max-w-[160px]">{b.property_name || '—'}</p>
                      {b.room_number && <p className="text-[11px] text-gray-500">Room {b.room_number}</p>}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">{b.booking_date || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-800 font-medium whitespace-nowrap">{b.check_in_date}</td>
                    <td className="px-3 py-3 text-sm font-semibold text-purple-700 whitespace-nowrap">₹{(b.token_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">
                      <p>₹{(b.rent_amount || 0).toLocaleString()}</p>
                      <p className="text-[11px] text-gray-500">Dep ₹{(b.deposit_amount || 0).toLocaleString()}</p>
                    </td>
                    <td className="px-3 py-3"><span className={`text-[11px] px-2 py-1 rounded-full font-medium ${s.bg} ${s.text}`}>{s.label}</span></td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {b.status === 'pending' && (
                          <button data-testid={`booking-confirm-${b.id}`} onClick={() => handleConfirm(b)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Mark as confirmed (token collected)"><CheckCircle size={14} /></button>
                        )}
                        {b.status !== 'moved_in' && b.status !== 'converted' && b.status !== 'cancelled' && (
                          <button data-testid={`booking-convert-${b.id}`} onClick={() => handleConvert(b)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Move In (create tenant)"><LogIn size={14} /></button>
                        )}
                        {b.status !== 'moved_in' && b.status !== 'converted' && b.status !== 'cancelled' && (
                          <button data-testid={`booking-cancel-${b.id}`} onClick={() => setCancellingBooking(b)} className="p-1.5 text-orange-600 hover:bg-orange-50 rounded" title="Cancel booking"><XCircle size={14} /></button>
                        )}
                        <button data-testid={`booking-edit-${b.id}`} onClick={() => { setEditing(b); setShowForm(true); }} className="p-1.5 text-gray-600 hover:bg-gray-50 rounded" title="Edit"><Edit2 size={14} /></button>
                        <button data-testid={`booking-delete-${b.id}`} onClick={() => setDeleting(b)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <BookingFormModal
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSuccess={fetchData}
          editing={editing}
          token={token}
        />
      )}

      <DeleteConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete Booking"
        message="Are you sure you want to delete this booking?"
        itemName={deleting ? `${deleting.name} - ₹${deleting.token_amount}` : ''}
        loading={deleteLoading}
      />

      {/* Move-In flow: pre-filled Add Tenant modal that posts to /bookings/:id/move-in */}
      {movingInBooking && (
        <AddTenantModal
          isOpen={!!movingInBooking}
          onClose={() => setMovingInBooking(null)}
          onSuccess={() => { setMovingInBooking(null); fetchData(); }}
          token={token}
          bookingId={movingInBooking.id}
          prefill={{
            property_id: movingInBooking.property_id || '',
            room_id: movingInBooking.room_id || '',
            name: movingInBooking.name || '',
            phone: movingInBooking.phone || '',
            email: movingInBooking.email || '',
            check_in_date: movingInBooking.check_in_date || new Date().toISOString().split('T')[0],
            rent_amount: movingInBooking.rent_amount || '',
            deposit_amount: movingInBooking.deposit_amount || '',
            // Carry token info so the move-in modal can ask "adjust to rent/deposit?"
            token_amount: movingInBooking.token_amount || 0,
            token_paid: !!movingInBooking.token_paid,
            token_method: movingInBooking.token_method || '',
          }}
        />
      )}
      {/* Cancel flow — asks owner to refund or forfeit the token */}
      {cancellingBooking && (
        <CancelBookingModal
          open={!!cancellingBooking}
          onClose={() => setCancellingBooking(null)}
          onSuccess={() => { setCancellingBooking(null); fetchData(); }}
          booking={cancellingBooking}
          token={token}
        />
      )}
    </div>
  );
};

const BookingFormModal = ({ open, onClose, onSuccess, editing, token }) => {
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    property_id: '', room_id: '',
    booking_date: new Date().toISOString().split('T')[0],
    check_in_date: '',
    token_amount: 0, rent_amount: 0, deposit_amount: 0,
    notice_period_days: 30,
    notes: '',
  });

  useEffect(() => {
    (async () => {
      const res = await axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } });
      setProperties(res.data);
    })();
    if (editing) {
      setForm({
        name: editing.name || '', phone: editing.phone || '', email: editing.email || '',
        property_id: editing.property_id || '', room_id: editing.room_id || '',
        booking_date: editing.booking_date || '',
        check_in_date: editing.check_in_date || '',
        token_amount: editing.token_amount || 0,
        rent_amount: editing.rent_amount || 0,
        deposit_amount: editing.deposit_amount || 0,
        notice_period_days: editing.notice_period_days || 30,
        notes: editing.notes || '',
      });
    }
  }, [token, editing]);

  useEffect(() => {
    if (!form.property_id) { setRooms([]); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/properties/${form.property_id}/rooms`, { headers: { Authorization: `Bearer ${token}` } });
        setRooms(res.data);
      } catch { setRooms([]); }
    })();
  }, [form.property_id, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/bookings/${editing.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        emit(EVT.BOOKING, { action: 'updated', id: editing.id });
        emit(EVT.ROOM,    { action: 'booking_changed' });
        track('booking_updated', { id: editing.id, token_paid: !!form.token_paid });
        toast({ title: 'Booking Updated' });
      } else {
        await axios.post(`${API}/bookings`, form, { headers: { Authorization: `Bearer ${token}` } });
        emit(EVT.BOOKING, { action: 'created' });
        emit(EVT.ROOM,    { action: 'booking_changed' });
        track('booking_created', { token_paid: !!form.token_paid, token_amount: Number(form.token_amount) || 0 });
        trackFirstTime('first_booking_created', { token_paid: !!form.token_paid });
        toast({ title: 'Booking Created' });
      }
      onSuccess();
      onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? 'Edit Booking' : 'New Booking'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input data-testid="booking-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
            <div><Label>Phone *</Label><Input data-testid="booking-phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required /></div>
            <div className="col-span-2"><Label>Email</Label><Input data-testid="booking-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            <div>
              <Label>Property *</Label>
              <select data-testid="booking-property" value={form.property_id} onChange={e => setForm({...form, property_id: e.target.value, room_id: ''})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
                <option value="">Select</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Room (Optional)</Label>
              <select data-testid="booking-room" value={form.room_id} onChange={e => setForm({...form, room_id: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" disabled={!form.property_id}>
                <option value="">Select Room</option>
                {rooms.map(r => <option key={r.id} value={r.id}>Room {r.room_number}</option>)}
              </select>
            </div>
            <div><Label>Booking Date *</Label><Input data-testid="booking-date" type="date" value={form.booking_date} onChange={e => setForm({...form, booking_date: e.target.value})} required /></div>
            <div><Label>Check-in Date *</Label><Input data-testid="booking-checkin" type="date" value={form.check_in_date} onChange={e => setForm({...form, check_in_date: e.target.value})} required /></div>
            <div><Label>Token Amount (₹)</Label><Input data-testid="booking-token" type="number" value={form.token_amount} onChange={e => setForm({...form, token_amount: parseFloat(e.target.value) || 0})} /></div>
            <div><Label>Rent (₹)</Label><Input data-testid="booking-rent" type="number" value={form.rent_amount} onChange={e => setForm({...form, rent_amount: parseFloat(e.target.value) || 0})} /></div>
            <div><Label>Deposit (₹)</Label><Input data-testid="booking-deposit" type="number" value={form.deposit_amount} onChange={e => setForm({...form, deposit_amount: parseFloat(e.target.value) || 0})} /></div>
            <div>
              <Label>Notice Period (T&amp;C)</Label>
              <select
                data-testid="booking-notice-period"
                value={form.notice_period_days}
                onChange={e => setForm({...form, notice_period_days: parseInt(e.target.value, 10) || 30})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                title="Days the tenant must give before vacating. Each tenant can have a different period — agreed at booking time."
              >
                <option value={7}>7 days</option>
                <option value={15}>15 days</option>
                <option value={30}>30 days</option>
                <option value={45}>45 days</option>
              </select>
            </div>
            <div className="col-span-2"><Label>Notes</Label><textarea data-testid="booking-notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="booking-submit" type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update' : 'Create')}</Button>
            <Button data-testid="booking-cancel" type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingsList;
